import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { FloatingParticles } from './ParticleAnimation';
import {
  Users,
  Network,
  Cpu,
  Server,
  Settings2,
  Package,
  ArrowRight,
} from 'lucide-react';
import { useState } from 'react';

const products = [
  {
    value: 'plugplay',
    label: 'Plug & Play Systems',
    icon: Settings2,
    badge: 'HRIS & Supply Chain',
    color: 'primary',
    description: (
      <>
        <b>Plug & Play Systems</b> offers two main solutions: <b>HRIS</b> and <b>Supply Chain</b>.<br /><br />
        <b>HRIS</b> is a cloud-based platform for comprehensive Human Resource Management and payroll processes, providing integrated features to manage various HR aspects such as attendance, leave, performance management, and tax calculation.<br /><br />
        <b>Supply Chain</b> is a cloud-based software solution that enables companies to manage, optimize, and automate various supply chain functions, from procurement, inventory, production, to distribution—all accessible via subscription and internet.<br />
      </>
    ),
    hrisFeatures: [
      'Online Attendance & Leave',
      'Automated Payroll & Tax',
      'Performance Management',
    ],
    supplyChainFeatures: [
      'Procurement & Inventory',
      'Production & Distribution',
      'Cloud Access & Subscription',
    ],
    features: [], // for type safety
  },
  {
    value: 'iot',
    label: 'IoT Systems',
    icon: Network,
    badge: 'Internet of Things',
    color: 'accent',
    description: (
      <>
        Our <b>IoT Systems</b> solution integrates physical devices with digital platforms to monitor, control, and automate business processes in real-time. With advanced sensors and connectivity, companies gain accurate data, predictive analytics, and improved operational efficiency and security. Ideal for industries such as manufacturing, logistics, agriculture, and smart buildings.
      </>
    ),
    features: [
      'Sensor & Device Integration',
      'Real-time Monitoring',
      'Predictive Analytics',
      'Business Process Automation',
      'Cloud Connectivity',
      'Data Security',
    ],
    hrisFeatures: [], // for type safety
    supplyChainFeatures: [], // for type safety
  },
  {
    value: 'hardware',
    label: 'Electronic Hardware',
    icon: Cpu, // Changed from Chip to Cpu
    badge: 'Custom Electronics',
    color: 'secondary',
    description: (
      <>
        We provide custom electronic and hardware development services tailored to your business needs. From design and prototyping to mass production, our team helps create innovative hardware solutions for industrial automation, IoT devices, control systems, and consumer electronics—supported by the latest technology and high-quality standards.
      </>
    ),
    features: [
      'Design & Prototyping',
      'Mass Production',
      'Industrial Automation',
      'IoT Devices',
      'Control Systems',
      'High Quality',
    ],
    hrisFeatures: [], // for type safety
    supplyChainFeatures: [], // for type safety
  },
];

export function ProductSection() {
  const [activeTab, setActiveTab] = useState('plugplay');
  const [tabAnimation, setTabAnimation] = useState('');

  // Handler untuk tab change
  const handleTabChange = (value: string) => {
    if (value !== activeTab) {
      setTabAnimation('animate-back-in-right'); // pakai animasi yang sama dengan TeamSection
      setTimeout(() => {
        setActiveTab(value);
        setTabAnimation(''); // reset animasi setelah selesai
      }, 800); // durasi animasi harus sama dengan CSS .animate-back-in-right
    }
  };

  return (
    <section id="products" className="py-20 relative overflow-hidden">
      {/* Floating Particles */}
      <FloatingParticles 
        density={18}
        size="medium"
        colors={["#FFD700", "#00FFFF", "#00AEEF"]}
        className="opacity-30"
      />
      <div className="absolute inset-0 digital-grid opacity-10"></div>
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-accent/20 text-accent border-accent/30 font-orbitron">
            Our Products
          </Badge>
          <h2 className="text-4xl md:text-5xl font-orbitron font-bold text-accent mb-6">
            Plug & Play Technology
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-accent to-primary mx-auto mb-8"></div>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Ready-to-use technology solutions for your modern business needs.
          </p>
        </div>
        <div className="flex flex-col items-center">
          <Tabs
            defaultValue="plugplay"
            value={activeTab}
            onValueChange={handleTabChange}
            className="w-full max-w-4xl"
          >
            <TabsList className="mx-auto mb-8 bg-card/60 shadow-lg w-full max-w-full flex flex-col md:flex-row gap-2">
              {products.map((product) => (
                <TabsTrigger
                  key={product.value}
                  value={product.value}
                  className={`font-orbitron px-4 py-2 text-base rounded-xl transition-all duration-300 min-w-[180px] whitespace-nowrap flex-shrink-0 data-[state=active]:shadow-lg data-[state=active]:text-${product.color} data-[state=active]:bg-${product.color}/10`}
                >
                  <product.icon className={`w-6 h-6 mr-2 text-${product.color}`} />
                  {product.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {products.map((product) => (
              <TabsContent
                key={product.value}
                value={product.value}
                className={`focus:outline-none transition-all duration-400 ${activeTab === product.value && tabAnimation ? tabAnimation : ''}`}
              >
                <Card className={`bg-card/80 backdrop-blur-lg border-${product.color}/30 shadow-xl p-8 md:p-12 flex flex-col md:flex-row items-center md:items-start gap-8 transition-all duration-300 hover:shadow-${product.color}/40`}> 
                  <div className="flex-shrink-0 flex flex-col items-center md:items-start w-full md:w-1/3">
                    <div className={`w-24 h-24 bg-gradient-to-br from-${product.color}/20 to-${product.color}/5 rounded-2xl flex items-center justify-center mb-6 shadow-lg`}>
                      <product.icon className={`w-12 h-12 text-${product.color}`} />
                    </div>
                    <Badge className={`mb-2 bg-${product.color}/10 text-${product.color} border-${product.color}/30 font-orbitron`}>{product.badge}</Badge>
                  </div>
                  <div className="flex-1 w-full">
                    <h3 className={`text-2xl font-orbitron font-bold text-${product.color} mb-4`}>{product.label}</h3>
                    <div className="text-muted-foreground mb-6 leading-relaxed text-base">{product.description}</div>
                    {product.value === 'plugplay' ? (
                      <div className="flex flex-col md:flex-row gap-6 mb-8">
                        <div className="flex-1">
                          <h4 className="font-semibold text-primary mb-2">HRIS Features</h4>
                          <ul className="space-y-2">
                            {product.hrisFeatures.map((feature, idx) => (
                              <li key={idx} className="flex items-center space-x-3">
                                <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                                <span className="text-sm text-muted-foreground">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="flex-1 mt-6 md:mt-0">
                          <h4 className="font-semibold text-primary mb-2">Supply Chain Features</h4>
                          <ul className="space-y-2">
                            {product.supplyChainFeatures.map((feature, idx) => (
                              <li key={idx} className="flex items-center space-x-3">
                                <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                                <span className="text-sm text-muted-foreground">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                        {product.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center space-x-3">
                            <div className={`w-2 h-2 bg-${product.color} rounded-full animate-pulse`}></div>
                            <span className="text-sm text-muted-foreground">{feature}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className={`group mt-2 border border-${product.color}/20 hover:border-${product.color}/40 w-fit px-6 font-orbitron text-${product.color} hover:bg-${product.color}/10 transition-all duration-300`}
                    >
                      <span className="relative z-10">Learn More</span>
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                    </Button>
                  </div>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>
    </section>
  );
}
